import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';

import {
  DATE_MASK_MAX_LENGTH,
  formatDateFieldValue,
  parseDateFieldValue,
  toIsoDate,
} from '../../lib/date-mask.js';
import { cn } from '../../lib/utils.js';
import { ShellIconCalendar } from '../patterns/app-shell/shell-stroke-icon.js';

import { InputGroup } from './input-group.js';
import { Input } from './input.js';

export interface DateInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'max'
> {
  invalid?: boolean;
  /** ISO `YYYY-MM-DD` */
  value?: string | null;
  /** ISO `YYYY-MM-DD` */
  defaultValue?: string;
  /** Inclusive ISO `YYYY-MM-DD` bound for the native picker. Typed values after max still notify `onChange`. */
  max?: string;
  onChange?: (isoDate: string | null) => void;
  /** Fires on text blur with committed ISO (null when empty or still invalid) and masked display. */
  onIsoBlur?: (isoDate: string | null, maskedValue: string) => void;
}

function isIsoAfterMax(iso: string, max: string | undefined): boolean {
  return max !== undefined && max.length > 0 && iso > max;
}

/** Data pt-BR: exibição `dd/mm/aaaa` + ícone abre seletor nativo (`lang="pt-BR"`). Wire ISO `YYYY-MM-DD`. */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  {
    className,
    invalid = false,
    value,
    defaultValue,
    onChange,
    onIsoBlur,
    onBlur,
    id,
    max,
    'aria-label': ariaLabel,
    disabled,
    ...props
  },
  ref,
) {
  const fallbackId = useId();
  const textId = id ?? fallbackId;
  const nativePickerId = `${textId}-picker`;
  const nativeRef = useRef<HTMLInputElement>(null);
  const isControlled = value !== undefined;

  const [masked, setMasked] = useState(() =>
    formatDateFieldValue(defaultValue ?? value ?? undefined),
  );
  const [internalIso, setInternalIso] = useState(defaultValue ?? '');
  const syncedValueRef = useRef(value);

  useEffect(() => {
    if (!isControlled) {
      return;
    }
    if (value === syncedValueRef.current) {
      return;
    }
    syncedValueRef.current = value;
    setMasked(formatDateFieldValue(value));
  }, [isControlled, value]);

  const nativeIso = isControlled
    ? (value ?? toIsoDate(masked) ?? '')
    : (toIsoDate(masked) ?? internalIso);

  function commitFromMask(nextMasked: string) {
    setMasked(nextMasked);
    const nextIso = toIsoDate(nextMasked);
    if (nextIso) {
      syncedValueRef.current = nextIso;
      onChange?.(nextIso);
      if (isIsoAfterMax(nextIso, max)) {
        return;
      }
      setInternalIso(nextIso);
      return;
    }
    if (nextMasked.trim().length === 0) {
      setInternalIso('');
      syncedValueRef.current = null;
      onChange?.(null);
    }
  }

  function commitFromNative(nextIso: string) {
    if (nextIso && isIsoAfterMax(nextIso, max)) {
      return;
    }
    const nextMasked = formatDateFieldValue(nextIso);
    setMasked(nextMasked);
    setInternalIso(nextIso);
    syncedValueRef.current = nextIso ? nextIso : null;
    onChange?.(nextIso ? nextIso : null);
  }

  function handleMaskedChange(event: ChangeEvent<HTMLInputElement>) {
    commitFromMask(parseDateFieldValue(event.target.value));
  }

  function handleNativeChange(event: ChangeEvent<HTMLInputElement>) {
    commitFromNative(event.target.value);
  }

  function handleMaskedBlur(event: FocusEvent<HTMLInputElement>) {
    const isoFromMask = toIsoDate(masked);
    const committedIso =
      masked.trim().length === 0
        ? null
        : (isoFromMask ??
          (isControlled ? (value ?? null) : internalIso.trim().length > 0 ? internalIso : null));
    onIsoBlur?.(committedIso, masked);
    onBlur?.(event);
  }

  const pickerAriaLabel = ariaLabel ? `Abrir calendário — ${ariaLabel}` : 'Abrir calendário';

  return (
    <div className="date-input" lang="pt-BR">
      <InputGroup hasSuffix className={className}>
        <Input
          ref={ref}
          id={textId}
          value={masked}
          onChange={handleMaskedChange}
          onBlur={handleMaskedBlur}
          inputMode="numeric"
          placeholder="DD/MM/AAAA"
          maxLength={DATE_MASK_MAX_LENGTH}
          aria-label={ariaLabel}
          invalid={invalid}
          disabled={disabled}
          {...props}
        />
        <label className={cn('input-suffix', 'date-input__suffix')}>
          <input
            ref={nativeRef}
            id={nativePickerId}
            type="date"
            lang="pt-BR"
            className="date-input__native"
            value={nativeIso}
            onChange={handleNativeChange}
            aria-label={pickerAriaLabel}
            disabled={disabled}
            {...(max !== undefined && max.length > 0 ? { max } : {})}
          />
          <ShellIconCalendar size={14} aria-hidden />
        </label>
      </InputGroup>
    </div>
  );
});
